import { createStore, select, withProps } from '@ngneat/elf';
import { localStorageStrategy, persistState } from '@ngneat/elf-persist-state';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { TColorModes, TPanelSections } from './app.repository';
import { TAppLanguage } from '../shared/schemas/settings';

const USER_SETTINGS_STORE_NAME = 'andorUserSettings.v1';

// Condensed the mic and speakers state to avoid double networking requests to the SFU.
export enum AudioMuteStates {
  none,
  mic,
  speakersKeepMic,
  speakers,
}

export function dec2hex(dec: number): string {
  return dec.toString(16).padStart(2, '0');
}
export function generateId(length?: number): string {
  const arr = new Uint8Array((length || 64) / 2);
  window.crypto.getRandomValues(arr);
  return Array.from(arr, dec2hex).join('');
}

// Two ways to render the chat timeline:
//   - 'left'  — every message aligns to the left; own and remote are
//               distinguished only by sender name + glass tint. Default;
//               familiar from Slack/Discord/IRC clients.
//   - 'split' — own messages flip to the right edge of the row, remote stay
//               on the left. Familiar from messaging apps (WhatsApp, iMessage).
export type ChatLayout = 'left' | 'split';

// How long chat messages are kept on this device. 'off' disables persistence
// entirely (and removes the persisted history); the durations are enforced by
// `MessageStore` pruning everything older than the window.
export type ChatRetention = 'off' | '1w' | '1m' | '1y';

export const CHAT_RETENTION_MS: Record<Exclude<ChatRetention, 'off'>, number> = {
  '1w': 7 * 24 * 60 * 60 * 1000,
  '1m': 30 * 24 * 60 * 60 * 1000,
  '1y': 365 * 24 * 60 * 60 * 1000,
};

export interface UserSettingsState {
  // Canonical room identity (server-minted roomId). Used everywhere a room is
  // identified: connect/rejoin, chat/activity/notification scoping. Distinct
  // from `roomName`, which is the human display name (NOT unique).
  roomId: string;
  roomName: string;
  userName: string;
  userId: string;
  avatar: string;
  mutedState: AudioMuteStates;
  inputVolume: number;
  roomVolume: number;
  volumeGate: number;
  voiceActivityDetection: boolean;
  stopMicAccessOnMute: boolean;
  useWebRtc: boolean;
  playChatSounds: boolean;
  playJoinedLeftSounds: boolean;
  playAppSounds: boolean;
  useNotifications: boolean;
  requestPermissionsCheck: boolean;
  visibleTiles: string;
  accessKey: string;
  serverAddress: string;
  isSFUConnection: boolean;
  colorMode: TColorModes;
  usersListExpanded: boolean;
  panelClassMain: TPanelSections;
  panelClassPinned: TPanelSections | null;
  talkPermissionGranted: boolean;
  // Legacy slot for the pre-retention count-based cap. New installs never
  // read it; kept on the state shape so the one-time migration below can
  // detect an old "Off" (0) choice in persisted settings.
  maxMessagesPerRoom: number;
  chatRetention: ChatRetention;
  chatLayout: ChatLayout;
  // Interface language chosen by the user. `null` follows the browser's
  // language preferences (resolved by `LanguageService`), including later
  // changes to them.
  language: TAppLanguage | null;
}
const userSettingsStore = createStore(
  { name: USER_SETTINGS_STORE_NAME },
  withProps<UserSettingsState>({
    userName: '',
    roomId: '',
    roomName: '',
    // Legacy slot for the pre-refactor client-generated userId. New installs
    // leave this empty; the app reads its canonical id from the server's
    // andorUUID. Kept on the state shape so the one-time migration in
    // app.service can detect old-format entries.
    userId: '',
    avatar: '',
    mutedState: AudioMuteStates.mic,
    roomVolume: 1,
    inputVolume: 1,
    volumeGate: -35,
    voiceActivityDetection: true,
    stopMicAccessOnMute: true,
    useWebRtc: false,
    playJoinedLeftSounds: false,
    playChatSounds: true,
    playAppSounds: true,
    useNotifications: true,
    requestPermissionsCheck: true,
    visibleTiles: 'auto',
    accessKey: '',
    serverAddress: '',
    isSFUConnection: false,
    colorMode: null,
    usersListExpanded: true,
    panelClassMain: 'video',
    panelClassPinned: 'chat',
    talkPermissionGranted: false,
    maxMessagesPerRoom: 200,
    chatRetention: '1m',
    chatLayout: 'left',
    language: null,
  })
);

persistState(userSettingsStore, {
  key: USER_SETTINGS_STORE_NAME,
  storage: localStorageStrategy,
});

userSettingsStore.update((state) => ({
  ...state,
  mutedState:
    state.mutedState === AudioMuteStates.none ? AudioMuteStates.none : AudioMuteStates.mic,
}));

// One-time migration: count-based `maxMessagesPerRoom` → time-based
// `chatRetention`. Legacy 0 meant "Off" (persistence disabled) — a privacy
// choice that must not silently flip to persisting, so it maps to 'off'. All
// other legacy values map to the '1m' default, which `withProps` already
// supplies — no write needed. Resetting the legacy slot to 200 in the same
// update makes this one-shot; otherwise a later retention choice would flip
// back to 'off' on next boot. Safe at module scope: the localStorage
// rehydration above is synchronous.
userSettingsStore.update((state) =>
  state.maxMessagesPerRoom === 0
    ? { ...state, chatRetention: 'off' as const, maxMessagesPerRoom: 200 }
    : state
);

// If the user has never set a color mode, infer it from the system preference
// (`prefers-color-scheme`). Once set, the user's choice is persisted and used
// on subsequent loads regardless of system changes.
if (
  userSettingsStore.getValue().colorMode === null &&
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function'
) {
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  userSettingsStore.update((state) => ({
    ...state,
    colorMode: prefersDark ? 'dark' : 'light',
  }));
}

@Injectable({
  providedIn: 'root',
})
export class UserSettingsStore {
  get store() {
    return userSettingsStore;
  }
  store$ = this.store.pipe(select((state) => state));
  get value(): UserSettingsState {
    return this.store.getValue();
  }
  userName$ = this.store.pipe(select((state) => state.userName));
  roomId$ = this.store.pipe(select((state) => state.roomId));
  roomName$ = this.store.pipe(select((state) => state.roomName));
  avatar$ = this.store.pipe(select((state) => state.avatar));
  roomVolume$ = this.store.pipe(select((state) => state.roomVolume));
  inputVolume$ = this.store.pipe(select((state) => state.inputVolume));
  mutedState$ = this.store.pipe(select((state) => state.mutedState));
  volumeGate$ = this.store.pipe(select((state) => state.volumeGate));
  playJoinedLeftSounds$ = this.store.pipe(select((state) => state.playJoinedLeftSounds));
  playAppSounds$ = this.store.pipe(select((state) => state.playAppSounds));
  playChatSounds$ = this.store.pipe(select((state) => state.playChatSounds));

  voiceActivityDetection$ = this.store.pipe(select((state) => state.voiceActivityDetection));
  stopMicAccessOnMute$ = this.store.pipe(select((state) => state.stopMicAccessOnMute));

  useWebRTC$ = this.store.pipe(select((state) => state.useWebRtc));
  visibleTiles$ = this.store.pipe(select((state) => state.visibleTiles));
  usersListExpanded$ = this.store.pipe(select((state) => state.usersListExpanded));
  panelClassMain$ = this.store.pipe(select((state) => state.panelClassMain));
  panelClassPinned$ = this.store.pipe(select((state) => state.panelClassPinned));
  colorMode$ = this.store.pipe(select((state) => state.colorMode));
  chatRetention$ = this.store.pipe(select((state) => state.chatRetention));
  chatLayout$ = this.store.pipe(select((state) => state.chatLayout));
  language$ = this.store.pipe(select((state) => state.language));

  get colorMode(): TColorModes {
    return this.store.getValue().colorMode;
  }

  get inverseColorMode(): TColorModes {
    switch (this.store.getValue().colorMode) {
      case 'dark':
        return 'light';
      case 'light':
        return 'dark';
      default:
        return null;
    }
  }

  micActive$(): Observable<boolean> {
    return this.mutedState$.pipe(
      map((state) => {
        return state === AudioMuteStates.none;
      })
    );
  }

  speakersActive$(): Observable<boolean> {
    return this.mutedState$.pipe(
      map((state) => {
        return state === AudioMuteStates.none || state === AudioMuteStates.mic;
      })
    );
  }

  getSidePanelClass$(): Observable<'expanded' | ''> {
    return this.store$.pipe(
      select((state) => state.usersListExpanded),
      map((state) => {
        if (state) {
          return 'expanded';
        } else {
          return '';
        }
      })
    );
  }

  /**
   * Switch between the 1- and 2-column layout. Pinning keeps the current main
   * module and pins chat next to it (or video, when chat is the main module).
   */
  togglePinnedModule() {
    this.store.update((state) => {
      if (state.panelClassPinned !== null) {
        return { ...state, panelClassPinned: null };
      }
      const pinned: TPanelSections = state.panelClassMain === 'chat' ? 'video' : 'chat';
      return { ...state, panelClassPinned: pinned };
    });
  }

  toggleColorMode() {
    this.store.update((state) => ({
      ...state,
      colorMode: state.colorMode === 'light' ? 'dark' : 'light',
    }));
  }

  update(app: Partial<UserSettingsState>) {
    userSettingsStore.update((state) => ({
      ...state,
      ...app,
    }));
  }
}
